<template>
  <section class="container my-75 md:my-100 text-center">
    <div class="max-w-screen-xl mx-auto">
      <h2
        v-if="dataRef?.entry?.heading2 && !hideTitle"
        class="wysiwyg-title text-4xl md:text-5xl lg:text-6xl mb-20"
        v-html="dataRef.entry.heading2"
      />
      <div
        v-if="dataRef?.entry?.intro2"
        class="wysiwyg max-w-screen-md mx-auto text-lg"
        v-html="dataRef.entry.intro2"
      />

      <div
        v-if="dataRef?.posts?.length"
        class="grid sm:grid-cols-2 lg:grid-cols-3 gap-30 mt-60"
      >
        <div
          v-for="item in dataRef?.posts"
          :key="`post-${item.id}`"
          class="text-left relative"
        >
          <span
            :class="[
              'bg-white px-10 py-5 font-medium text-md text-secondary absolute left-15 top-15',
              { hidden: hideLabel },
            ]"
          >
            {{ linkLabel(item.typeHandle).label }}
          </span>
          <Media
            v-if="item.image?.length"
            :media="item.image[0]"
            :alt="item.title"
            loading="lazy"
            width="800"
            height="800"
          />
          <div class="pt-20">
            <h3 class="font-bold text-xl md:text-2xl font-serif leading-none">
              {{ item.title }}
            </h3>
            <NuxtLink
              :to="`/${item.uri}`"
              class="inline-flex items-center text-xl font-serif underline space-x-5 group mt-15 transition-colors hover:text-primary-700 text-primary-500"
            >
              <span>{{ linkLabel(item.typeHandle).button }}</span>
              <ChevronIcon class="w-20 -rotate-90 group-hover:translate-x-5 transition" />
            </NuxtLink>
          </div>
        </div>
      </div>

      <ClientOnly>
        <InfiniteLoading
          :distance="300"
          :first-load="false"
          @infinite="infinite"
        >
          <template #spinner>
            <div class="flex justify-center mt-100">
              <img
                src="@/assets/img/icon-loader.png"
                alt="loading..."
                class="w-[60px] h-[60px] animate-spin"
              >
            </div>
          </template>
          <template #complete>
            <span />
          </template>
          <template #error="{ retry }">
            <div class="flex justify-center mt-100">
              {{ $t('general.error') }}
              <button @click="retry">
                &nbsp;{{ $t('general.retry') }}
              </button>
            </div>
          </template>
        </InfiniteLoading>
      </ClientOnly>
    </div>
  </section>
</template>

<script setup>
import {
  useI18n,
  useAsyncGql,
  useRoute,
  shallowRef,
  ref,
} from '#imports';
import InfiniteLoading from 'v3-infinite-loading';

// Assets
import ChevronIcon from '@/assets/img/icon-chevron.svg';

// Props
const props = defineProps({
  hideTitle: {
    type: Boolean,
  },
  variables: {
    type: Object,
    required: true,
  },
  data: {
    type: Object,
    required: true,
  },
  offset: {
    type: Number,
    required: true,
  },
  hideLabel: {
    type: Boolean,
  },
});

// Emitters
const emit = defineEmits(['update:offset']);

const { t } = useI18n({ useScope: 'global' });
const route = useRoute();
const offsetRef = ref(props.offset);
const dataRef = shallowRef(props.data);

// Methods
/**
 * Infinite loader
 *
 * @param {Object} $state
 */
const infinite = async ($state) => {
  try {
    // Run query again with offset and limit
    offsetRef.value += props.variables.limit;
    const result = await useAsyncGql(route.meta?.handle, {
      ...props.variables,
      offset: offsetRef.value,
    });

    // Fetched entries
    const fetchedEntries = result.data.value?.posts;

    // Append data
    dataRef.value?.posts.push(...fetchedEntries);

    // Set loading state
    if (fetchedEntries.length < props.variables.limit) {
      $state.complete();
    } else {
      $state.loaded();
    }

    emit('update:offset', offsetRef.value);
  } catch {
    $state.error();
  }
};

/**
 * Renders call to action based on typeHandle blogpost
 *
 * @param   {String}  handle post type
 *
 * @return  {String}
 */
const linkLabel = (handle) => {
  switch (handle) {
    case 'makeover':
      return {
        label: t('blog.label.makeover'),
        button: t('blog.label.button.makeover'),
      };
    case 'interiorViewers':
      return {
        label: t('blog.label.interior-viewers'),
        button: t('blog.label.button.interior-viewers'),
      };
    case 'interiorStyles':
      return {
        label: t('blog.label.interior-styles'),
        button: t('blog.label.button.interior-styles'),
      };
    default:
      return {
        label: t('blog.label.blog'),
        button: t('blog.label.button.blog'),
      };
  }
};
</script>
